'use client';

import { useState, useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { IconFileTypePdf, IconPhoto, IconFlask } from '@tabler/icons-react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';

import {
  a4Dimensions,
  downloadAsPdf,
  downloadImagesSequentially,
  generateContainerPages,
  type QrMode
} from '../lib/generate-container-pages';

const QR_MODES: QrMode[] = ['match', 'mismatch', 'none'];
const DPI_OPTIONS = [150, 200, 300];

/**
 * Generates synthetic A4 container pages with optional baked-in QR codes —
 * download as one PDF (matches the "Upload PDF" flow on the photo-scanning
 * page) or as a sequence of JPEGs (matches the "Upload Images" flow). Lets QA
 * exercise every backend branch (match / mismatch / unverified) and the new
 * stored-bbox overlay without ever printing a real sheet.
 */
export function ContainerGenerator() {
  const t = useTranslations('devTools.container');
  const [containerNumber, setContainerNumber] = useState('CNT-DEV-001');
  const [pageCount, setPageCount] = useState(8);
  const [qrMode, setQrMode] = useState<QrMode>('match');
  const [dpi, setDpi] = useState(200);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const previewUrlRef = useRef<string | null>(null);

  // Live preview — re-render a single sample page off the same generator so
  // operators see what their settings will produce before they download a 50-
  // page PDF.
  useEffect(() => {
    if (!containerNumber.trim()) {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
      setPreviewUrl(null);
      return;
    }

    let cancelled = false;
    const handle = window.setTimeout(async () => {
      try {
        // Preview is always rendered at 150 DPI to keep the canvas small —
        // this is a UI-only sample, not the bytes the user downloads.
        const dims = a4Dimensions(150);
        const [first] = await generateContainerPages({
          containerNumber: containerNumber.trim() || 'CNT-DEV-001',
          pageCount: 1,
          qrMode,
          widthPx: dims.width,
          heightPx: dims.height
        });
        if (cancelled || !first) return;
        const url = URL.createObjectURL(first.blob);
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = url;
        setPreviewUrl(url);
        setPreviewError(null);
      } catch (err) {
        if (cancelled) return;
        setPreviewUrl(null);
        setPreviewError(err instanceof Error ? err.message : String(err));
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [containerNumber, qrMode]);

  // Final cleanup of the preview blob URL when the component unmounts so we
  // don't leak object URLs across hot reloads.
  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    };
  }, []);

  const handleDownload = async (format: 'pdf' | 'images') => {
    const trimmed = containerNumber.trim();
    if (!trimmed) {
      toast.error(t('noContainerToast'));
      return;
    }
    if (!Number.isFinite(pageCount) || pageCount < 1 || pageCount > 200) {
      toast.error(t('invalidPageCountToast'));
      return;
    }

    setIsGenerating(true);
    setProgress(0);
    try {
      const dims = a4Dimensions(dpi);
      const pages = await generateContainerPages({
        containerNumber: trimmed,
        pageCount,
        qrMode,
        widthPx: dims.width,
        heightPx: dims.height,
        onProgress: (fraction) => setProgress(Math.round(fraction * 100))
      });

      if (format === 'pdf') {
        await downloadAsPdf(pages, `${trimmed}.pdf`);
        toast.success(t('pdfDownloadedToast', { count: pages.length }));
      } else {
        downloadImagesSequentially(pages, trimmed);
        toast.success(t('imagesDownloadedToast', { count: pages.length }));
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('generateFailedToast'));
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className='flex items-center gap-2'>
          <IconFlask className='size-5 text-amber-500' />
          {t('title')}
        </CardTitle>
        <CardDescription>{t('description')}</CardDescription>
      </CardHeader>
      <CardContent className='flex flex-col gap-6'>
        <div className='grid gap-4 md:grid-cols-2'>
          <div className='space-y-2'>
            <Label htmlFor='dev-container-number'>{t('containerLabel')}</Label>
            <Input
              id='dev-container-number'
              value={containerNumber}
              onChange={(e) => setContainerNumber(e.target.value)}
              placeholder='CNT-DEV-001'
              autoComplete='off'
            />
            <p className='text-muted-foreground text-xs'>
              {t('containerHint')}
            </p>
          </div>

          <div className='space-y-2'>
            <Label htmlFor='dev-page-count'>{t('pageCountLabel')}</Label>
            <Input
              id='dev-page-count'
              type='number'
              min={1}
              max={200}
              value={pageCount}
              onChange={(e) => {
                const next = Number.parseInt(e.target.value, 10);
                setPageCount(Number.isFinite(next) ? next : 1);
              }}
            />
            <p className='text-muted-foreground text-xs'>
              {t('pageCountHint')}
            </p>
          </div>

          <div className='space-y-2'>
            <Label>{t('qrModeLabel')}</Label>
            <Select
              value={qrMode}
              onValueChange={(value) => setQrMode(value as QrMode)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {QR_MODES.map((m) => (
                  <SelectItem key={m} value={m}>
                    {t(`qrMode_${m}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className='text-muted-foreground text-xs'>
              {t(`qrModeHint_${qrMode}`)}
            </p>
          </div>

          <div className='space-y-2'>
            <Label>{t('dpiLabel')}</Label>
            <Select
              value={String(dpi)}
              onValueChange={(value) => setDpi(Number(value))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DPI_OPTIONS.map((d) => (
                  <SelectItem key={d} value={String(d)}>
                    {t('dpiOption', { dpi: d })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className='text-muted-foreground text-xs'>{t('dpiHint')}</p>
          </div>
        </div>

        <div className='flex flex-wrap items-center gap-3'>
          <Button
            onClick={() => handleDownload('pdf')}
            disabled={isGenerating}
            className='gap-2'
          >
            <IconFileTypePdf className='size-4' />
            {t('downloadPdfButton')}
          </Button>
          <Button
            variant='outline'
            onClick={() => handleDownload('images')}
            disabled={isGenerating}
            className='gap-2'
          >
            <IconPhoto className='size-4' />
            {t('downloadImagesButton')}
          </Button>
          {isGenerating && (
            <div className='flex-1 min-w-[180px]'>
              <div className='mb-1 flex justify-between text-xs'>
                <span className='text-muted-foreground'>
                  {t('progressLabel')}
                </span>
                <span className='text-muted-foreground'>{progress}%</span>
              </div>
              <Progress value={progress} />
            </div>
          )}
        </div>

        <div className='space-y-2'>
          <h4 className='text-sm font-medium'>{t('previewLabel')}</h4>
          {previewError ? (
            <p className='text-destructive text-sm'>{previewError}</p>
          ) : previewUrl ? (
            <div className='flex justify-center rounded-md border bg-slate-50 p-4 dark:bg-slate-900'>
              {/* eslint-disable-next-line @next/next/no-img-element -- dev-tools-only blob preview; next/image can't process a runtime ObjectURL */}
              <img
                src={previewUrl}
                alt={t('previewAlt')}
                className='max-h-[480px] w-auto rounded shadow-sm'
              />
            </div>
          ) : (
            <p className='text-muted-foreground text-sm'>{t('previewEmpty')}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
